import { Check } from 'lucide-react'
import { BRAND } from '@/constants/brand'
import { cn } from '@/utils/cn'

export const WIZARD_STEP_LABELS = ['Input', 'Scan', 'Confirm', 'Submit'] as const

export type WizardStepsProps = {
  /** 1-based step index (1–4). */
  current: number
  labels?: readonly string[]
  className?: string
}

export function WizardSteps({
  current,
  labels = WIZARD_STEP_LABELS,
  className,
}: WizardStepsProps) {
  const total = labels.length
  const step = Math.min(Math.max(1, current), total)
  const progress = step / total

  return (
    <div
      className={cn(
        'border-b border-border bg-card pb-3',
        className
      )}
    >
      <div className="w-full px-5 pt-3 sm:px-7 lg:px-9">
        <div className="h-1 w-full overflow-hidden rounded-full bg-border" style={{ height: 4 }}>
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${progress * 100}%`, backgroundColor: BRAND }}
          />
        </div>
      </div>

      <ol className="mt-3 flex w-full items-start gap-2 px-5 sm:px-7 lg:px-9">
        {labels.map((label, i) => {
          const n = i + 1
          const active = n === step
          const done = n < step

          return (
            <li key={`${label}-${n}`} className="flex min-w-0 flex-1 flex-col items-center gap-[5px]">
              <span
                className={cn(
                  'inline-flex items-center justify-center rounded-full border-[1.5px] text-[11px] font-black',
                  !active && !done && 'border-border bg-muted text-muted-foreground'
                )}
                style={{
                  width: 26,
                  height: 26,
                  ...(active
                    ? { backgroundColor: BRAND, borderColor: BRAND, color: '#fff' }
                    : done
                      ? { backgroundColor: '#10B981', borderColor: '#10B981', color: '#fff' }
                      : undefined),
                }}
              >
                {done && !active ? (
                  <Check className="h-3 w-3" strokeWidth={3} />
                ) : (
                  n
                )}
              </span>
              <span
                className={cn(
                  'w-full truncate text-center font-extrabold',
                  !active && !done && 'text-muted-foreground',
                  done && !active && 'text-emerald-500'
                )}
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  ...(active ? { color: BRAND } : undefined),
                }}
              >
                {label}
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
